import React, { useContext, useMemo, useState, useEffect } from 'react';
import { Menu, Skeleton } from 'antd';
import { ProductsContext } from '../../context/ProductsContext';
import { useLocation } from 'react-router-dom';
import usePermissions from '../../hooks/usePermissions';
import { stripAccountPrefix } from '../../constants/permissions';
import { useActiveAccountOptional } from '../../hooks/useActiveAccount';
import { VIRTUAL_AUDITOR_PRODUCT_CODE } from '../../utils/virtualAgentPlan';
import {
  HomeOutlined,
  PhoneOutlined,
  RobotOutlined,
  BookOutlined,
  AuditOutlined,
  ApiOutlined,
  CommentOutlined,
  CoffeeOutlined,
  CheckCircleOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';

const iconsArray = {
  NTP: <BookOutlined />,
  NTI: <ApiOutlined />,
  NTU: <RobotOutlined />,
  NTHSVOI: <PhoneOutlined />,
  NTHSSMS: <CommentOutlined />,
  NTAM: <AuditOutlined />,
  NTS: <CoffeeOutlined />,
  NTPV: <CheckCircleOutlined />,
  NTRV: <RobotOutlined />,
  NTHW: <WhatsAppOutlined />,
};

const VIRTUAL_AUDITORS_PATH = '/service/virtual-auditors';

const SidebarMenu = ({ navigatePath, collapsed, setCollapsed }) => {
  const { allProducts, loading } = useContext(ProductsContext);
  const location = useLocation();
  const { filterViewsByPermission, canPath } = usePermissions();
  const account = useActiveAccountOptional();
  // Mientras NTVAA no exista en el catalogo de /products/v1 no hay vista que lo
  // liste, asi que lo agregamos a mano solo si el cliente tiene el producto.
  const ownsVirtualAuditors = (account?.clientInfo?.products || []).includes(
    VIRTUAL_AUDITOR_PRODUCT_CODE
  );

  const normalizeKey = (value) => {
    if (!value) return '/';
    const str = String(value).trim();
    return str.startsWith('/') ? str.toLowerCase() : `/${str.toLowerCase()}`;
  };

  const menuItems = useMemo(() => {
    const items = [
      {
        icon: <HomeOutlined />,
        key: '/home',
        label: 'Inicio',
      },
    ];

    const catalog = Array.isArray(allProducts) ? allProducts : [];

    catalog.forEach((item) => {
      const visibleViews = filterViewsByPermission(item.views || []);
      if (visibleViews.length > 0) {
        const children = visibleViews.map((task) => ({
          key: normalizeKey(task.path),
          label: task.name,
        }));

        items.push({
          icon: iconsArray[item.id] || <RobotOutlined />,
          key: item.id,
          label: item.comercialName,
          children,
        });
        return;
      }

      const missingPath = normalizeKey(`service/${item.comercialName}`);
      if (!canPath(missingPath)) {
        return;
      }
      items.push({
        icon: iconsArray[item.id] || <RobotOutlined />,
        key: missingPath,
        label: item.comercialName,
      });
    });

    const hasVirtualAuditors = items.some(
      (item) =>
        item.key === VIRTUAL_AUDITORS_PATH ||
        (item.children || []).some((child) => child.key === VIRTUAL_AUDITORS_PATH)
    );
    if (ownsVirtualAuditors && canPath(VIRTUAL_AUDITORS_PATH) && !hasVirtualAuditors) {
      items.push({
        icon: <AuditOutlined />,
        key: VIRTUAL_AUDITORS_PATH,
        label: 'Auditores virtuales',
      });
    }

    return items;
  }, [canPath, filterViewsByPermission, allProducts, ownsVirtualAuditors]);

  const selectedKey = useMemo(
    () => normalizeKey(stripAccountPrefix(location.pathname)),
    [location.pathname]
  );
  const computedOpenKey = useMemo(() => {
    const match = (allProducts || []).find((product) =>
      filterViewsByPermission(product.views || []).some(
        (v) => normalizeKey(v.path) === selectedKey
      )
    );
    return match ? match.id : undefined;
  }, [allProducts, selectedKey, filterViewsByPermission]);

  const [openKeys, setOpenKeys] = useState(
    computedOpenKey ? [computedOpenKey] : []
  );
  useEffect(() => {
    setOpenKeys(computedOpenKey ? [computedOpenKey] : []);
  }, [computedOpenKey]);

  return loading ? (
    <div style={{ padding: '16px' }}>
      <Skeleton active paragraph={{ rows: 10 }} />
    </div>
  ) : (
    <Menu
      theme="dark"
      mode="inline"
      items={menuItems}
      onClick={({ key }) => navigatePath(key)}
      selectedKeys={[selectedKey]}
      openKeys={openKeys}
      onOpenChange={(keys) => setOpenKeys(keys)}
      inlineCollapsed={collapsed}
    />
  );
};

export default SidebarMenu;
